import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useState,
  type ReactNode,
} from "react"
import { toast } from "react-toastify"

import { FOLDER_DELETING_REASON } from "./packageDisplay"
import { freeName, topFolderId } from "./packageFolders"
import type { PackageWizardMode } from "./PackageWizard"
import { usePackageDraft } from "./setup/packageDraft"
import type { PackageFolder, RangePackage } from "./types"

export type PackageSelection = { kind: "package" | "folder"; id: string } | null
type Target = NonNullable<PackageSelection>

/** Rename… or Delete… picked on a rail row: the page of that item carries it out. */
type PageRequest = { kind: "rename" | "delete"; target: Target; nonce: number }

type MoveStores = "keep" | "follow"
export type PendingPackageMove = { pkg: RangePackage; folder: PackageFolder; stores: MoveStores }
export type PendingFolderMove = { folder: PackageFolder; parent: PackageFolder }

/**
 * What the rail rows, the page headers and the folder page share: the
 * selection, the package draft every change is staged into (so busy is the
 * same everywhere), creating, moving and reordering. A move within one
 * top-level folder is staged straight away; into another folder it asks about
 * stores first, and a folder landing on a folder asks before it becomes a
 * subfolder.
 */
function useNavigatorValue() {
  const [selection, setSelection] = useState<PackageSelection>(null)
  const [request, setRequest] = useState<PageRequest | null>(null)
  const [wizardMode, setWizardMode] = useState<PackageWizardMode | null>(null)
  const [pendingMove, setPendingMove] = useState<PendingPackageMove | null>(null)
  const [pendingFolderMove, setPendingFolderMove] = useState<PendingFolderMove | null>(null)
  const draft = usePackageDraft()
  const { packages, folders } = draft
  const folderById = new Map(folders.map((folder) => [folder.id, folder]))
  const select = setSelection

  const { stage } = draft

  const applyMove = (packageId: string, folderId: string | null, stores: MoveStores) =>
    stage(
      { kind: "movePackage", packageId, folderId, stores },
      {
        onDone: () => {
          setPendingMove(null)
          const folder = folderId === null ? undefined : folderById.get(folderId)
          toast.success(folder ? `Moved to ${folder.name}` : "Moved to the top level")
        },
      },
    )

  const movePackage = (packageId: string, folderId: string | null) => {
    const pkg = packages.find((other) => other.id === packageId)
    if (!pkg || pkg.folderId === folderId) return
    const folder = folderId === null ? undefined : folderById.get(folderId)
    const sameTop =
      folder !== undefined &&
      pkg.folderId !== null &&
      topFolderId(folders, pkg.folderId) === topFolderId(folders, folder.id)
    if (!folder || sameTop) return applyMove(packageId, folderId, "keep")
    setPendingMove({ pkg, folder, stores: "keep" })
  }

  const applyFolderMove = (folderId: string, parentId: string | null) =>
    stage({ kind: "moveFolder", folderId, parentId }, { onDone: () => setPendingFolderMove(null) })

  const moveFolder = (folderId: string, parentId: string | null) => {
    const folder = folderById.get(folderId)
    if (!folder || folder.parentId === parentId) return
    const parent = parentId === null ? undefined : folderById.get(parentId)
    if (!parent) return applyFolderMove(folderId, null)
    setPendingFolderMove({ folder, parent })
  }

  /** Creates the folder, then opens it, or hands its id to `then`. */
  const createFolder = (
    name: string,
    parentId: string | null,
    then?: (folderId: string) => void,
  ) => {
    const folderId = crypto.randomUUID()
    stage(
      { kind: "createFolder", folderId, parentId, window: null, name },
      { onDone: () => (then ? then(folderId) : select({ kind: "folder", id: folderId })) },
    )
  }

  const newSubfolderName = (parentId: string) =>
    freeName(
      "New subfolder",
      folders.filter((other) => other.parentId === parentId).map((other) => other.name),
    )

  const removeSubfolder = (folder: PackageFolder) => {
    const parent = folder.parentId === null ? undefined : folderById.get(folder.parentId)
    if (!parent) return
    stage(
      { kind: "removeSubfolder", folderId: folder.id },
      {
        onDone: () => {
          if (selection?.kind === "folder" && selection.id === folder.id) {
            select({ kind: "folder", id: parent.id })
          }
          toast.success(`Subfolder removed · its packages now sit directly in ${parent.name}`)
        },
      },
    )
  }

  const reorder = (kind: "package" | "folder", ids: string[]) =>
    stage({
      kind: "reorder",
      packageIds: kind === "package" ? ids : [],
      folderIds: kind === "folder" ? ids : [],
    })

  return {
    packages,
    folders,
    draft,
    selection,
    select,
    /** While a publish runs; staging queues, so it never blocks. */
    busy: draft.saving,
    stage,
    request,
    /** Opens the item's page and asks it to start renaming or deleting. */
    ask: (kind: PageRequest["kind"], target: Target) => {
      select(target)
      setRequest({ kind, target, nonce: Date.now() })
    },
    handled: () => setRequest(null),
    wizardMode,
    newPackage: (folderId: string | null) => setWizardMode({ kind: "create", folderId }),
    editRange: (pkg: RangePackage) => setWizardMode({ kind: "editRange", pkg }),
    closeWizard: () => setWizardMode(null),
    createFolder,
    /** New subfolder…: a free "New subfolder" name under the folder. */
    createSubfolder: (parentId: string, then?: (folderId: string) => void) =>
      createFolder(newSubfolderName(parentId), parentId, then),
    /** Move to › New subfolder…: a new subfolder in the package's top-level folder, with the package in it. */
    moveIntoNewSubfolder: (pkg: RangePackage) => {
      if (pkg.folderId === null) return
      const topId = topFolderId(folders, pkg.folderId)
      createFolder(newSubfolderName(topId), topId, (folderId) =>
        applyMove(pkg.id, folderId, "keep"),
      )
    },
    movePackage,
    pendingMove,
    chooseMoveStores: (stores: MoveStores) => setPendingMove((prev) => prev && { ...prev, stores }),
    cancelMove: () => setPendingMove(null),
    confirmMove: () => {
      if (pendingMove) applyMove(pendingMove.pkg.id, pendingMove.folder.id, pendingMove.stores)
    },
    /** Why a folder can't take new packages or moves: it's marked for deletion, and they'd be deleted on Publish. */
    folderDeletingReason: (folderId: string | null) =>
      folderId !== null && draft.isFolderDeleting(folderId) ? FOLDER_DELETING_REASON : undefined,
    moveFolder,
    pendingFolderMove,
    cancelFolderMove: () => setPendingFolderMove(null),
    confirmFolderMove: () => {
      if (pendingFolderMove) {
        applyFolderMove(pendingFolderMove.folder.id, pendingFolderMove.parent.id)
      }
    },
    removeSubfolder,
    reorder,
  }
}

export type PackageNavigator = ReturnType<typeof useNavigatorValue>

const NavigatorContext = createContext<PackageNavigator | null>(null)

export function PackageNavigatorProvider({ children }: { children: ReactNode }) {
  const value = useNavigatorValue()
  return <NavigatorContext.Provider value={value}>{children}</NavigatorContext.Provider>
}

export function usePackageNavigator() {
  const value = useContext(NavigatorContext)
  if (!value) throw new Error("usePackageNavigator needs PackageNavigatorProvider")
  return value
}

/** Carries out a Rename… or Delete… picked on this item's rail row. */
export function usePageRequest(target: Target, handlers: Record<PageRequest["kind"], () => void>) {
  const { request, handled } = usePackageNavigator()
  const run = useEffectEvent((kind: PageRequest["kind"]) => {
    handlers[kind]()
    handled()
  })
  const mine = request?.target.kind === target.kind && request.target.id === target.id
  useEffect(() => {
    if (mine && request) run(request.kind)
  }, [mine, request])
}
